import Dexie from 'dexie';
import { applySchema } from './schema';
import { applyEncryption } from './encryption';

/**
 * Per-user database manager
 * 
 * Each user gets their own IndexedDB database, named by their email hash.
 * This provides:
 * - Complete data isolation between users
 * - Encrypted data that's only readable with the user's encryption key
 * - Seamless re-login for the same user (data persists)
 * - Clean slate for different users
 */

const DATABASE_PREFIX = 'jurybox_';

// Preserve the open database across Vite HMR module reloads in development.
const hotData = import.meta.hot?.data;
let currentDb = hotData?.currentDb ?? null;
let currentUserHash = hotData?.currentUserHash ?? null;
// The active user's encryption key. The DBCore middleware holds its own
// reference for cases/jurors; this lets repository-layer code (PendingRepository)
// encrypt specific fields with the same key. Null when running without
// encryption (e.g. pre-auth or tests that pass no key).
let currentEncryptionKey = hotData?.currentEncryptionKey ?? null;

if (import.meta.hot) {
  import.meta.hot.dispose((data) => {
    data.currentDb = currentDb;
    data.currentUserHash = currentUserHash;
    data.currentEncryptionKey = currentEncryptionKey;
  });
}

/**
 * Hash email to create database name
 * Uses the same hashing as encryptedStorage for consistency
 */
async function hashEmail(email) {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 16);
}

/**
 * Get database name for a user
 */
async function getDatabaseName(userEmail) {
  const hash = await hashEmail(userEmail);
  return `${DATABASE_PREFIX}${hash}`;
}

/**
 * Initialize or get the database for a specific user
 * @param {string} userEmail - User's email address
 * @param {CryptoKey} encryptionKey - User's encryption key
 * @returns {Promise<Dexie>} - Configured Dexie database instance
 */
export async function initializeDatabase(userEmail, encryptionKey) {
  if (!userEmail) {
    throw new Error('User email is required to initialize database');
  }

  const userHash = await hashEmail(userEmail);
  
  // If same user, return existing db
  if (currentDb && currentUserHash === userHash) {
    if (!currentDb.isOpen()) {
      await currentDb.open();
    }
    // Refresh the key in case it became available after a keyless open.
    if (encryptionKey) currentEncryptionKey = encryptionKey;
    console.log('Returning existing database for user');
    return currentDb;
  }

  // Close existing db if different user
  if (currentDb) {
    console.log('Closing previous user database');
    currentDb.close();
    currentDb = null;
    currentUserHash = null;
  }

  const dbName = await getDatabaseName(userEmail);
  console.log(`Initializing database: ${dbName}`);

  // Create new Dexie instance
  const db = new Dexie(dbName);

  // Apply schema
  applySchema(db);

  // Apply encryption middleware if key provided
  if (encryptionKey) {
    applyEncryption(db, encryptionKey);
  }
  currentEncryptionKey = encryptionKey ?? null;

  // Open the database
  await db.open();

  // Store user email in sync metadata
  await db.syncMetadata.put({ key: 'userEmail', value: userEmail });

  currentDb = db;
  currentUserHash = userHash;

  console.log(`Database initialized for user: ${userEmail.substring(0, 3)}***`);

  // Notify hooks that the database is available. useDatabase only polls
  // briefly after mount, so late initialization (e.g. retried after a server
  // outage) needs an explicit signal to reach already-mounted components.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('jurybox:db-ready'));
  }

  return db;
}

/**
 * Get the current database instance
 * @returns {Dexie|null} - Current database or null if not initialized
 */
export function getDatabase() {
  return currentDb;
}

/**
 * Check if database is initialized and ready
 * @returns {boolean}
 */
export function isDatabaseReady() {
  return currentDb !== null && currentDb.isOpen();
}

/**
 * Close the current database
 * Called on logout to ensure clean state
 */
export function closeDatabase() {
  if (currentDb) {
    console.log('Closing database');
    currentDb.close();
    currentDb = null;
    currentUserHash = null;
    currentEncryptionKey = null;
  }
}

/**
 * Get the active user's encryption key (or null when running unencrypted).
 * Used by repository-layer code that encrypts specific fields with the same
 * key the DBCore middleware uses for cases/jurors.
 * @returns {CryptoKey|null}
 */
export function getEncryptionKey() {
  return currentEncryptionKey;
}

/**
 * Delete the database for a specific user
 * Called on explicit logout to optionally clear data
 * @param {string} userEmail - User's email address
 */
export async function deleteDatabase(userEmail) {
  const dbName = await getDatabaseName(userEmail);
  
  // Close if it's the current db
  if (currentDb) {
    closeDatabase();
  }

  console.log(`Deleting database: ${dbName}`);
  await Dexie.delete(dbName);
}

/**
 * Check if a database exists for a user
 * @param {string} userEmail - User's email address
 * @returns {Promise<boolean>}
 */
export async function databaseExists(userEmail) {
  const dbName = await getDatabaseName(userEmail);
  const databases = await Dexie.getDatabaseNames();
  return databases.includes(dbName);
}

/**
 * List all Jurybox databases (for debugging/admin)
 * @returns {Promise<string[]>}
 */
export async function listAllDatabases() {
  const databases = await Dexie.getDatabaseNames();
  return databases.filter(name => name.startsWith(DATABASE_PREFIX));
}

/**
 * Get database for use in React hooks
 * Throws if not initialized (components should wait for auth)
 */
export function requireDatabase() {
  if (!currentDb) {
    throw new Error('Database not initialized. User must be logged in.');
  }
  return currentDb;
}

// Alias for SyncEngine usage
export const getDb = getDatabase;

// Export the current db instance for direct access in hooks
// Note: This will be null until initializeDatabase is called
export { currentDb as db };

// Re-export sync modules
export { default as SyncEngine } from './sync/SyncEngine';
export { default as WebSocketClient } from './sync/WebSocketClient';

// Re-export migration utilities
export { migrateLegacyData, needsMigration, getMigrationInfo } from './migration';

export default {
  initializeDatabase,
  getDatabase,
  getDb,
  isDatabaseReady,
  closeDatabase,
  deleteDatabase,
  databaseExists,
  listAllDatabases,
  requireDatabase
};
